import { GamesService } from '../../../services/games/games.service';
import { filter, map, switchMap, tap } from 'rxjs/operators';
import { NEVER, Observable, of } from 'rxjs';
import { Component, Input, OnInit, inject } from '@angular/core';
import { LastPlayedGamesService } from '../../../services/games/last-played-games.service';
import { FavoriteGamesService } from '../../../services/games/favorite-games.service';
import { Router } from '@angular/router';
import { FreespinsGamesService } from '../../../services/games/freespins-games.service';
import { GameCategory } from '../../../services/games/game-category';
import { TranslatePipe } from '../../../services/translation/translate.pipe';
import { IgnoreHtmlTagsPipe } from '../../pipes/ignore-html-tags.pipe';
import { SafePipe } from '../../pipes/safe.pipe';
import { SkeletonComponent } from '../skeleton/skeleton.component';
import { FittextDirective } from '../../directives/fit-text.directive';
import { GamePreviewComponent } from '../game-preview/game-preview.component';
import { NgClass, AsyncPipe } from '@angular/common';
import { GameFilterService } from '../../../services/games/game-filter.service';

export interface GameListConfig {
  filter?: any;
  title?: string;
  link?: string;
  btnName?: string;
  gameList?: any[];
  hideTitle?: boolean;
  type?: 'favorite' | 'last-played' | 'tournament' | 'advance-games' | 'free-spins';
}

@Component({
  selector: 'app-game-list',
  templateUrl: './game-list.component.html',
  styleUrls: ['./game-list.component.scss'],
  imports: [NgClass, GamePreviewComponent, FittextDirective, SkeletonComponent, AsyncPipe, SafePipe, IgnoreHtmlTagsPipe, TranslatePipe],
})
export class GameListComponent {
  private _games = inject(GamesService);
  private _lastPlayed = inject(LastPlayedGamesService);
  private _favorite = inject(FavoriteGamesService);
  private _freespinsGames = inject(FreespinsGamesService);
  private _router = inject(Router);
  private _gamesFilters = inject(GameFilterService);


  @Input('config') set config(value: GameListConfig) {
    this._config = value;
    this.list$ = this._reducerGameList();
  }

  private _config: GameListConfig;

  public list$: Observable<any> = NEVER;

  public showMore: boolean = null;

  public isEmpty: boolean = null;

  get onGetConfig(): GameListConfig {
    return this._config;
  }

  /**
   * Return game list by game type: default, last-played, favorite
   * @returns
   */
  private _reducerGameList(): Observable<any> {
    let list: Observable<any>;
    switch (this._config.type) {
      case 'favorite':
        list = this._favorite.getMappedGameList().pipe(
          map(e => {
            e.gameList = e.gameList || [];
            e.gameList = e.gameList.slice(0, this._config.filter.limit);
            return e;
          }),
        );
        break;
      case 'last-played':
        list = this._lastPlayed.getMappedGameList().pipe(
          map(e => {
            e.gameList = e.gameList || [];
            e.gameList = e.gameList.slice(0, this._config.filter.limit);
            return e;
          }),
        );
        break;
      case 'free-spins':
        list = this._freespinsGames.getMappedGameList().pipe(
          switchMap((res) => {
            return this._games.list({
              category: GameCategory.THANK_YOU,
            }).pipe(
              filter(data => !!data),
              map(data => {
                const gameList = data?.gameList?.filter((e) => {
                  return !res?.gameList?.some((i) => e?.id === i?.id);
                });
                data.gameList = res?.gameList?.concat(gameList);
                return data;
              }),
            );
          }),
          map((e) => {
            e.gameList = e?.gameList || [];
            e.gameList = e?.gameList?.slice(0, this._config.filter.limit);
            return e;
          }),
        );
        break;
      case 'tournament':
        list = of(this._config).pipe(
          map(e => {
            e.gameList = this._games.gameListMapper(this._config?.gameList?.slice(0, this._config.filter.limit) || []);
            return e;
          }),
        );
        break;
      case 'advance-games':
        list = this._games.list(this._config?.filter, true, this._config.gameList).pipe(
          filter(data => !!data),
          map(data => {
            data?.gameList.splice(0, this._config?.gameList?.length, ...this._config?.gameList);
            data.gameList = data?.gameList.slice(0, this._config?.filter.limit);
            return data;
          }),
        );
        break;
      default:
        list = this._games.list(this._config?.filter);
        break;
    }
    return list.pipe(
      tap((e: any) => {
        this._resolveGameSize(e.gameList);
      }),
    );
  }

  /**
   * Hide link more if gameList size < config.filter.limit
   * or hide all list if gameList size === 0
   */
  private _resolveGameSize(gameList: any[]) {
    if (gameList.length >= this._config?.filter?.limit) {
      this.showMore = true;
    }
    if (!gameList.length && this._config?.type !== 'tournament') {
      this.isEmpty = true;
    }
  }

  async goToRoute() {
    if (this._gamesFilters.state().isShowSearchBar) {
      this._gamesFilters.toggleSearchPopup();
    }
    await this._router.navigateByUrl(this._config.link);
  }

}
